/**
 * JWT Authentication Service with Idle Timeout
 * Adapted from hcp-admin-frontend for Manager Dashboard
 */
import { sessionManager } from './sessionManager';
import { setStoreTimeZone } from '../../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

interface AuthTokenData {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: any;
  user_role?: string;
  expires_in: number;
  idle_timeout: number;
  warning_timeout: number;
  corporate?: {
    corporate_code: string;
    name: string;
  };
  store?: {
    store_code: string;
    name: string;
  };
}

interface TokenStorage {
  token: string;
  refreshToken: string;
  loginTime: number;
  lastActivity: number;
  expiresIn: number;
  idleTimeout: number;
  warningTimeout: number;
}

const TOKEN_STORAGE_KEY = 'managerTokenData';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

class AuthService {
  private activityListeners: Array<() => void> = [];
  private idleCheckInterval: NodeJS.Timeout | null = null;

  /**
   * Store authentication token and setup idle tracking
   */
  async setAuthToken(authData: AuthTokenData): Promise<void> {
    // Clear any existing auth data first
    this.clearAuth();
    const now = Date.now();

    const tokenStorage: TokenStorage = {
      token: authData.access_token,
      refreshToken: authData.refresh_token,
      loginTime: now,
      lastActivity: now,
      expiresIn: authData.expires_in * 1000, // Convert to milliseconds
      idleTimeout: Math.max(authData.idle_timeout, 3600) * 1000, // Minimum 1 hour, in ms
      warningTimeout: authData.warning_timeout * 1000 // Convert to milliseconds
    };

    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenStorage));

    // Store user data for compatibility
    localStorage.setItem('managerAuthenticated', 'true');
    localStorage.setItem('managerUser', JSON.stringify(authData.user));

    // Store user role
    const userRole = authData.user_role || authData.user.role;
    if (userRole) {
      localStorage.setItem('managerRole', userRole);
    }

    // Set managerUserData for authorization system
    localStorage.setItem('managerUserData', JSON.stringify({
      level: authData.user.level,
      corporate_id: authData.user.corporate_id,
      store_id: authData.user.store_id,
      corporate_code: authData.corporate?.corporate_code,
      store_code: authData.store?.store_code
    }));

    this.setupActivityTracking();

    // Update SessionManager with timeout values from backend (minimum 1 hour for managers)
    sessionManager.setSessionConfig(Math.max(authData.idle_timeout, 3600), authData.warning_timeout);

    // For users with corporate/store assignments, fetch string codes from numeric IDs
    if (authData.user.corporate_id && authData.user.store_id) {
      try {
        // These reads run right after the token is stored above, so send it as the
        // logged-in admin. The backend will require an admin token on these two
        // endpoints (see hcp/docs/v2/REQ-manager-dashboard-auth-on-corporate-store.md).
        const token = this.getAuthToken();
        const authHeaders: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        // Fetch corporates to get the string code from numeric FK ID
        const corporateResponse = await fetch(`${API_BASE_URL}/corporates/`, { headers: authHeaders });
        if (corporateResponse.ok) {
          const corporates = await corporateResponse.json();
          const userCorporate = corporates.find((corp: any) => corp.id === authData.user.corporate_id);
          if (userCorporate) {
            // Update managerUserData with resolved corporate_code
            const userData = JSON.parse(localStorage.getItem('managerUserData') || '{}');
            userData.corporate_code = userCorporate.corporate_code;
            localStorage.setItem('managerUserData', JSON.stringify(userData));
          }
        }

        // Fetch stores to get the string code from numeric FK ID
        const storeResponse = await fetch(`${API_BASE_URL}/stores/`, { headers: authHeaders });
        if (storeResponse.ok) {
          const stores = await storeResponse.json();
          const userStore = stores.find((store: any) => store.id === authData.user.store_id);
          if (userStore) {
            // Update managerUserData with resolved store_code
            const userData = JSON.parse(localStorage.getItem('managerUserData') || '{}');
            userData.store_code = userStore.store_code;
            // The store's IANA timezone drives what "today" means in the UI,
            // matching hcp-backend's TimezoneService.get_store_today.
            userData.store_timezone = userStore.timezone;
            localStorage.setItem('managerUserData', JSON.stringify(userData));
            setStoreTimeZone(userStore.timezone);
          }
        }

        // Dispatch event after async operations complete
        window.dispatchEvent(new CustomEvent('authChanged', {
          detail: { type: 'login', user: authData.user }
        }));
      } catch (error) {
        console.error('Failed to fetch corporate/store string codes:', error);
        // Still dispatch event even if API calls failed
        window.dispatchEvent(new CustomEvent('authChanged', {
          detail: { type: 'login', user: authData.user }
        }));
      }
    } else {
      // For high-level users or users without store/corporate, no async fetch needed
      window.dispatchEvent(new CustomEvent('authChanged', {
        detail: { type: 'login', user: authData.user }
      }));
    }
  }

  /**
   * Get current auth token
   * Note: Returns token even if expired - let backend return 401 to trigger refresh
   */
  getAuthToken(): string | null {
    const tokenData = this.getTokenData();
    if (!tokenData) return null;

    // Only check idle timeout (frontend-only concern)
    if (this.isIdleExpired(tokenData)) {
      console.log('Idle timeout exceeded, clearing auth');
      this.clearAuth();
      return null;
    }

    return tokenData.token;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    const tokenData = this.getTokenData();
    return tokenData ? tokenData.refreshToken : null;
  }

  /**
   * Update access token (called after refresh)
   */
  updateAccessToken(newAccessToken: string): void {
    const tokenData = this.getTokenData();
    if (!tokenData) return;

    tokenData.token = newAccessToken;
    tokenData.loginTime = Date.now();
    tokenData.lastActivity = Date.now();

    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  }

  /**
   * Update last activity timestamp
   */
  updateActivity(): void {
    const tokenData = this.getTokenData();
    if (!tokenData) return;

    tokenData.lastActivity = Date.now();
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('managerAuthenticated');
    localStorage.removeItem('managerUser');
    localStorage.removeItem('managerUserData');
    localStorage.removeItem('managerRole');

    // Back to the browser's own date until the next login resolves a store.
    setStoreTimeZone(null);

    this.removeActivityTracking();

    // Dispatch custom event to notify contexts of auth change
    window.dispatchEvent(new CustomEvent('authChanged', {
      detail: { type: 'logout' }
    }));
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAuthToken() !== null;
  }

  /**
   * Get user data from localStorage
   */
  getUserData(): any | null {
    try {
      const userData = localStorage.getItem('managerUser');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get store_id from localStorage (set during login)
   */
  getStoreId(): number | undefined {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.store_id;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }

  /**
   * Get store_code from localStorage (set during login)
   */
  getStoreCode(): string | undefined {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.store_code;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }

  /**
   * Get the store's IANA timezone from localStorage (set during login).
   * Undefined for users with no store assignment.
   */
  getStoreTimezone(): string | undefined {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.store_timezone;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }

  /**
   * Get corporate_code from localStorage (set during login)
   */
  getCorporateCode(): string | undefined {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.corporate_code;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }

  /**
   * Setup activity tracking
   */
  private setupActivityTracking(): void {
    this.removeActivityTracking();

    const updateActivity = () => this.updateActivity();

    ACTIVITY_EVENTS.forEach(eventType => {
      document.addEventListener(eventType, updateActivity, true);
      this.activityListeners.push(() => {
        document.removeEventListener(eventType, updateActivity, true);
      });
    });

    // Start periodic idle check
    this.idleCheckInterval = setInterval(() => {
      this.checkIdleTimeout();
    }, 60000); // Check every minute
  }

  /**
   * Remove activity tracking
   */
  private removeActivityTracking(): void {
    this.activityListeners.forEach(removeListener => removeListener());
    this.activityListeners = [];

    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
  }

  /**
   * Check idle timeout and logout if exceeded
   */
  private checkIdleTimeout(): void {
    const tokenData = this.getTokenData();
    if (!tokenData) return;

    if (this.isIdleExpired(tokenData)) {
      console.log('Idle timeout detected, logging out');
      this.clearAuth();
      window.location.href = '/login?session_expired=true';
    }
  }

  /**
   * Get token data from localStorage
   */
  private getTokenData(): TokenStorage | null {
    try {
      const data = localStorage.getItem(TOKEN_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  /**
   * Check if idle timeout is exceeded
   */
  private isIdleExpired(tokenData: TokenStorage): boolean {
    const now = Date.now();
    return (now - tokenData.lastActivity) > tokenData.idleTimeout;
  }

  /**
   * Get time remaining until idle timeout (in minutes)
   */
  getIdleTimeRemaining(): number {
    const tokenData = this.getTokenData();
    if (!tokenData) return 0;

    const now = Date.now();
    const timeSinceActivity = now - tokenData.lastActivity;
    const timeRemaining = tokenData.idleTimeout - timeSinceActivity;

    return Math.max(0, Math.floor(timeRemaining / (60 * 1000)));
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
